/**
 * ChatRoom — top-level layout matching legacy frame 22 of the SWF.
 * Hosts the five faithful panels (Admin / Evil Admin / Arnold Sound
 * Board / Random Sound Board / Options), the click-to-open ProfileWindow,
 * and the Walrus minigame that appears when /walrus fires.
 */
import { useEffect, useRef, useState } from 'react';
import { MessageList } from './MessageList.js';
import { UserList } from './UserList.js';
import { Input } from './Input.js';
import { Header } from './Header.js';
import { PmTabs } from './PmTabs.js';
import { C4InviteBanner, C4Board } from './Connect4.js';
import { EasterEggOverlay, type EasterEggOverlayHandle } from './EasterEggOverlay.js';
import { TopicBar } from './TopicBar.js';
import { ArrowsBackground } from './ArrowsBackground.js';
import { ProfileWindow } from './ProfileWindow.js';
import { OptionsWindow } from './OptionsWindow.js';
import { ColorShop } from './ColorShop.js';
import { savePref, emitChange } from '../lib/prefs.js';
import { SoundBoard } from './SoundBoard.js';
import { EvilAdminPanel } from './EvilAdminPanel.js';
import { AdminPanel } from './AdminPanel.js';
import { WalrusMinigame, type WalrusChoice } from './WalrusMinigame.js';
import { useChat, tabKey, profileRoomName } from '../hooks/useChat.js';
import { usePrefs } from '../hooks/usePrefs.js';
import { useAfkDetect } from '../hooks/useAfkDetect.js';
import { play as playSound } from '../lib/soundboard.js';
import type { UserListEntry } from '../lib/types.js';

type PanelId = 'admin' | 'evil' | 'arnold' | 'random' | 'options' | null;

export function ChatRoom() {
    const fxRef = useRef<EasterEggOverlayHandle | null>(null);
    const [prefs] = usePrefs();
    const {
        state, submit, sendRaw, room, switchRoom, openPm, setActive, closePm, clear,
        c4Invite, c4Accept, c4Decline, c4Move, c4Resign, c4Dismiss, c4Rematch,
    } = useChat({
        onEasterEgg: kind => {
            if (prefs.playEffects) fxRef.current?.fire(kind);
            // Walrus event → also pop the minigame for everyone
            if (kind === 'walrus') setWalrusOpen(true);
        },
        canPlaySound: () => prefs.playSounds,
        onScreenFx: (kind) => {
            // Toggle the matching CSS class on .chat-room. Faithful to the
            // legacy SWF: `Invert_Color` flipped the whole stage's blend
            // mode; `Rotate_Chat` rotated _root 180°. Both are toggles.
            setScreenFx(s => ({ ...s, [kind]: !s[kind] }));
        },
        onPmAlert: other => {
            setFlashPm(s => new Set(s).add(other));
            setTimeout(() => setFlashPm(s => {
                const n = new Set(s); n.delete(other); return n;
            }), 3500);
        },
    });
    const [profile, setProfile] = useState<UserListEntry | null>(null);
    const [panel,   setPanel]   = useState<PanelId>(null);
    const [walrusOpen, setWalrusOpen] = useState(false);
    const [flashPm, setFlashPm] = useState<Set<string>>(new Set());
    const [colorShopOpen, setColorShopOpen] = useState(false);
    /** Stage-wide effects toggled by legacy /invert + /rotate chat_cmds. */
    const [screenFx, setScreenFx] = useState<{ invert: boolean; rotate: boolean }>({ invert: false, rotate: false });

    const active = state.active;
    const key = tabKey(active);
    const messages = state.threads[key] ?? [];
    const users = state.users[room] ?? [];

    // AFK auto-detection: idle >5min → AFK (1), >30min → Asleep (7).
    useAfkDetect({
        currentStatus: users.find(u => u.id === state.me?.id)?.uStatus ?? 0,
        onChangeStatus: s => sendRaw({ cmd: 'user_status', room, usr: state.me?.name, s }),
        enabled: !!state.me,
    });
    const closed = state.closed[room];
    const banned = state.banned !== null;
    const muted  = state.mutedUntil !== null && (state.mutedUntil === 0 || state.mutedUntil > Math.floor(Date.now()/1000));

    const placeholder =
        banned ? 'You are banned from this room.' :
        muted  ? 'You are muted.' :
        active.kind === 'pm' ? `Message @${active.other}…` :
        closed?.closed ? 'Room is closed. Only mods can speak.' :
        'Type a message, or /help for commands';

    // Apply walrus choice locally + broadcast it as a system line so
    // everyone in the room sees who took the bucket vs slapped vs left.
    function handleWalrusChoice(c: WalrusChoice) {
        setWalrusOpen(false);
        if (c === 'bucket') {
            if (prefs.playEffects) fxRef.current?.fire('bucket');
            sendRaw({ cmd: 'chat_cmd', room, c: 'took_bucket' });
        } else if (c === 'slap') {
            if (prefs.playSounds) playSound('arnold/terminated');
            sendRaw({ cmd: 'chat_cmd', room, c: 'slapped_walrus' });
        } else {
            sendRaw({ cmd: 'chat_cmd', room, c: 'left_walrus_alone' });
        }
    }

    // Auto-clear walrus minigame after 8s if user hasn't picked
    useEffect(() => {
        if (!walrusOpen) return;
        const t = setTimeout(() => setWalrusOpen(false), 8000);
        return () => clearTimeout(t);
    }, [walrusOpen]);

    return (
        <div className={`chat-room ${screenFx.invert ? 'fx-screen-invert' : ''} ${screenFx.rotate ? 'fx-screen-rotate' : ''}`}>
            <Header
                me={state.me}
                wsState={state.wsState}
                pingMs={state.pingMs}
                openPanel={setPanel}
            />
            {(state.wsState === 'closed' || state.wsState === 'error') && (
                <div className="pc-conn-banner" role="status">
                    <span className="pc-conn-dot" /> Connection lost — reconnecting…
                    <button className="pc-conn-retry"
                            onClick={() => window.location.reload()}>
                        Reload
                    </button>
                </div>
            )}
            {!state.me && state.wsState !== 'closed' && state.wsState !== 'error' && (
                <div className="pc-loading-overlay">
                    <div className="pc-loading-card">
                        {state.wsState === 'open'
                            ? 'Authenticating…'
                            : 'Loading the FFR Chat…'}
                    </div>
                </div>
            )}
            <div className="chat-grid">
                <main className="chat-main">
                    {(state.topics[room] || state.me?.is_mod) && (
                        <TopicBar
                            room={room}
                            topic={state.topics[room]?.topic ?? ''}
                            setBy={state.topics[room]?.set_by ?? ''}
                            isMod={!!state.me?.is_mod}
                            onSetTopic={t => sendRaw({ cmd: 'set_topic', room, topic: t })}
                        />
                    )}
                    {state.c4Pending && (
                        <C4InviteBanner
                            pending={state.c4Pending}
                            onAccept={c4Accept}
                            onDecline={c4Decline}
                        />
                    )}
                    {state.c4Active && (
                        <C4Board
                            game={state.c4Active}
                            onMove={c4Move}
                            onResign={c4Resign}
                            onDismiss={c4Dismiss}
                            onRematch={c4Rematch}
                        />
                    )}
                    <div className="chat-main-stage">
                        <ArrowsBackground />
                        <MessageList messages={messages} meName={state.me?.name} threadKey={key} />
                        <EasterEggOverlay handleRef={fxRef} />
                    </div>
                </main>
                <UserList
                    users={users}
                    nowPlaying={state.nowPlaying}
                    onUser={(u) => {
                        if (!state.me) return;
                        setProfile(u);
                    }}
                />
            </div>
            <PmTabs
                active={active}
                pmList={state.pmList}
                unread={state.unread}
                rooms={state.users}
                profileRoom={profileRoomName()}
                flashPm={flashPm}
                myRank={state.me?.k}
                onSelectRoom={switchRoom}
                onSelectPm={other => setActive({ kind: 'pm', other })}
                onClosePm={closePm}
            />
            <Input disabled={banned} placeholder={placeholder} onSend={submit} users={users} />

            {profile && state.me && (
                <ProfileWindow
                    me={state.me}
                    target={profile}
                    room={room}
                    onClose={() => setProfile(null)}
                    onPm={openPm}
                    onAction={frame => sendRaw(frame)}
                    onC4Invite={c4Invite}
                />
            )}
            {panel === 'options' && (
                <OptionsWindow
                    onClose={() => setPanel(null)}
                    onSetStatus={s => sendRaw({ cmd: 'user_status', room, usr: state.me?.name, s })}
                    currentStatus={users.find(u => u.id === state.me?.id)?.uStatus ?? 0}
                    onOpenColorShop={() => setColorShopOpen(true)}
                />
            )}
            {colorShopOpen && (
                <ColorShop
                    onClose={() => setColorShopOpen(false)}
                    onApply={hex => {
                        savePref('colorOverride', hex);
                        emitChange();
                    }}
                />
            )}
            {panel === 'arnold'  && <SoundBoard board="arnold" room={room} onClose={() => setPanel(null)} onSend={sendRaw} />}
            {panel === 'random'  && <SoundBoard board="random" room={room} onClose={() => setPanel(null)} onSend={sendRaw} />}
            {panel === 'evil'    && state.me?.is_admin && (
                <EvilAdminPanel room={room} users={users} onClose={() => setPanel(null)} onSend={sendRaw} />
            )}
            {panel === 'admin'   && state.me?.is_admin && (
                <AdminPanel room={room} onClose={() => setPanel(null)} onSend={sendRaw} onClearChat={clear} />
            )}
            {walrusOpen && (
                <WalrusMinigame onChoose={handleWalrusChoice} />
            )}
        </div>
    );
}
